import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import { getSponsorList,getAllSponsorTypes,createSponsor,deleteSponsor,updateSponsor } from "../../../api";
import ConfirmationDialog from "../../../components/common/ConfirmationDialog/ConfirmationDialog";
import { useNightMode } from "../../../contexts/NightModeContext";
import Background from "../../Login/Background";
import PageHeader from "../../../components/common/PageHeader/PageHeader";
import { Search } from "lucide-react";
import ListCard from "../../../components/common/ListCard/ListCard";

const UserSponsor = () => {
  const { isNightMode } = useNightMode();
  const [modal, setModal] = useState(false);
  const toggle = () => setModal(!modal);
  const [sponsors, setSponsors] = useState<any>([]);
  const [originalSponsors, setSponsorsList] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [allSponsorTypes, setAllSponsorTypes] = useState<any[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; row: any | null }>({ isOpen: false, row: null });
  const [isLoading, setIsLoading] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [editPreviewImage, setEditPreviewImage] = useState(
    editData?.thumb ?? ""
  );

  const getErrorMessage = (error: any) => typeof error === "string" ? error : "";

  useEffect(() => {
    if (editData) {
      setEditPreviewImage(editData.thumb);
    }
  }, [editData]);

  const { slug } = useParams();      
  useEffect(() => {
    getSponsors();
    getSponsorTypes();
  }, [slug]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      sponsorSelection: (sponsors && sponsors.sponsorSelection) || "",
      sponsorName: (sponsors && sponsors.sponsorName) || "",
      sponsorWebsiteUrl: (sponsors && sponsors.sponsorWebsiteUrl) || "",
      thumb: (sponsors && sponsors.thumb) || "",
    },
    validationSchema: Yup.object({
      sponsorSelection: Yup.string().required("Selection is Required"),
      sponsorName: Yup.string().required("Sponsor Name is Required"),
      thumb: Yup.string().required("Image is Required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        setIsLoading(true);
        const eventData = localStorage.getItem("eventData");
        if (eventData) {
          const slug = JSON.parse(eventData).slugId;
          const formData = new FormData();
          formData.append("sponsorTypeId", values.sponsorSelection);
          formData.append("name", values.sponsorName);
          formData.append("link", values.sponsorWebsiteUrl || null);
          formData.append("eventSlug", slug);
          formData.append("thumb", values.thumb);
          await createSponsor(formData);
          toast.success("Sponsor created successfully");
          toggle();
          resetForm();
          getSponsors();
        }
      } catch (error) {
        toast.error("Failed to create sponsor");
      } finally {
        setIsLoading(false);
      }
    },
  });

  const editValidation = useFormik({ 
    enableReinitialize: true,
    initialValues: {
      sponsorId: editData?.sponsorId,
      sponsorSelection: editData?.sponsorTypeId  ?? '',
      sponsorName: editData?.name ?? '',
      sponsorWebsiteUrl: editData?.link ?? '',
      thumb: '' // initially empty
    },
    validationSchema: Yup.object({ 
      sponsorSelection: Yup.string().required("Selection is Required"),
      sponsorName: Yup.string().required("Sponsor Name is Required"),
    }),
    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        const formData = new FormData();
        formData.append("sponsorId", values.sponsorId);
        formData.append("sponsorTypeId", values.sponsorSelection);
        formData.append("name", values.sponsorName);
        formData.append("link", values.sponsorWebsiteUrl);
        if (values.thumb) {
          formData.append("thumb", values.thumb);
        }
        await updateSponsor(values.sponsorId, formData);
        toast.success("Sponsor updated successfully!");
        setEditModal(false);
        getSponsors();
      } catch (error) {
        toast.error("Failed to update sponsor.");
      } finally {
        setIsLoading(false);
      }
    },
  });

  const getSponsors = async () => {
    try {
      const eventData = localStorage.getItem("eventData");
      if (eventData) {
        const eventSlug = JSON.parse(eventData).slugId;
        const response = await getSponsorList();
        if (response && response.data) {
          const filteredAndSortedSponsors: any[] = [];
          response.data.forEach((sponsorItem: any) => {
            if (sponsorItem.eventSlug === eventSlug) {
              filteredAndSortedSponsors.push({
                title: sponsorItem.sponsorType?.title || "N/A", 
                name: sponsorItem.name,
                link: sponsorItem.link,
                thumb: sponsorItem.thumb,
                sponsorId: sponsorItem.sponsorId,
                sponsorTypeId: sponsorItem.sponsorTypeId,
                createdAt: sponsorItem.createdAt || sponsorItem.createdDate || "",
              });
            }
          });
          filteredAndSortedSponsors.sort((a, b) =>
            a.name.localeCompare(b.name)
          );

          setSponsors(filteredAndSortedSponsors);
          setSponsorsList(filteredAndSortedSponsors);
        }
      }
    } catch (error) {
      console.error("Error fetching sponsors:", error);
    } finally {
      setLoading(false);
    }
  };

  const getSponsorTypes = async () => {
    try {
      const response = await getAllSponsorTypes();
      setAllSponsorTypes(response);
    } catch (error) {
      console.error("Error fetching sponsor types:", error);
    }
  };

  const handleDelete = (row: any) => {
    setDeleteConfirm({ isOpen: true, row });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.row) return;
    try {
      await deleteSponsor(deleteConfirm.row.sponsorId);
      toast.success("Sponsor deleted successfully!");
      getSponsors();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || "Failed to delete sponsor";
      toast.error(errorMsg);
    } finally {
      setDeleteConfirm({ isOpen: false, row: null });
    }
  };

  const handleSearch = (e: any) => {
    const searchTerm = e.target.value;
    setSearch(searchTerm);
    if (searchTerm.length > 0) {
      const tempSponsors = originalSponsors.filter((sponsor: any) =>
        sponsor.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setSponsors(tempSponsors);
    } else {
      setSponsors(originalSponsors); 
    }
  };

  const handleEditClick = (item: any) => {
    setEditData(item);
    setEditPreviewImage(item.thumb);
    setEditModal(true);
  };

  return (
    <div className={`contact-list-container ${isNightMode ? 'text-white' : 'text-dark'}`}>
      <Background />
      <ToastContainer autoClose={2000} />
      <PageHeader
        title="Sponsors"
        subtitle="Manage event sponsors, sponsor tiers, website links, branding assets, and display orders."
        isLoading={loading}
        actions={
          <Button color="danger" onClick={toggle} className="v3-btn-primary h_40 d-flex align-items-center">
            <i className="fa-solid fa-plus me-2"></i> New Sponsor
          </Button>
        }
      />

      <div className="contact-filter-card mb-4 mt-4">
        <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
          <div className="position-relative flex-grow-1 w-100">
            <Search className="position-absolute start-0 top-50 translate-middle-y ms-3 contact-sub-text" size={18} />
            <input
              type="text"
              className="contact-search-input"
              placeholder="Search sponsors by name..."
              value={search}
              onChange={handleSearch}
            />
          </div>
        </div>
      </div>

      <div>
        <Modal isOpen={modal} toggle={toggle}>
          <ModalHeader toggle={toggle}>Add New Sponsor</ModalHeader>
          <ModalBody>
            <Form
              className="px-3"
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
                return false;
              }}
            >
              <FormGroup>
                <Label className="form-label">Name</Label>
                <Input
                  name="sponsorName"
                  type="text"
                  placeholder="Name"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.sponsorName || ""}
                  invalid={validation.touched.sponsorName && !!validation.errors.sponsorName}
                />
                <FormFeedback>{getErrorMessage(validation.errors.sponsorName)}</FormFeedback>
              </FormGroup>
              
              <FormGroup>
                <Label for="selectedEvent">Selection</Label>
                <Input
                  type="select"
                  id="selectedEvent"
                  name="sponsorSelection"
                  value={validation.values.sponsorSelection}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={validation.touched.sponsorSelection && !!validation.errors.sponsorSelection}
                >
                  <option value="">Select Sponsor Type</option>
                  {allSponsorTypes.map((type: any) => (
                    <option key={type.sponsorTypeId} value={type.sponsorTypeId}>
                      {type.title}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{getErrorMessage(validation.errors.sponsorSelection)}</FormFeedback>
              </FormGroup>

              <FormGroup>
                <Label className="form-label">Website URL</Label>
                <Input
                  name="sponsorWebsiteUrl"
                  type="text"
                  placeholder="Website URL"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.sponsorWebsiteUrl || ""}
                  invalid={validation.touched.sponsorWebsiteUrl && !!validation.errors.sponsorWebsiteUrl}
                />
                <FormFeedback>{getErrorMessage(validation.errors.sponsorWebsiteUrl)}</FormFeedback>
              </FormGroup>

              <FormGroup>
                <Label className="form-label" for="image">Upload Image</Label>
                <style>{`
                  .sponsor-file-input {
                    display: flex !important;
                    align-items: center !important;
                    height: 46px !important;
                    padding: 4px 8px !important;
                  }
                  .sponsor-file-input::-webkit-file-upload-button,
                  .sponsor-file-input::file-selector-button {
                    margin: 0 12px 0 0 !important;
                    border-radius: 6px !important;
                    height: 34px !important;
                    padding: 0 14px !important;
                    display: inline-flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    border: none !important;
                    background: #ffffff !important;
                    color: #222222 !important;
                    font-weight: 500 !important;
                    cursor: pointer !important;
                  }
                `}</style>
                <Input
                  id="image"
                  name="thumb"
                  type="file"
                  className="sponsor-file-input"
                  onChange={(event) => {
                    const file = event.currentTarget.files?.[0];
                    if (file) {
                      validation.setFieldValue("thumb", file);
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setEditPreviewImage(reader.result as string);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  onBlur={validation.handleBlur}
                  invalid={validation.touched.thumb && !!validation.errors.thumb}
                />
                <FormFeedback>{getErrorMessage(validation.errors.thumb)}</FormFeedback>
              </FormGroup>

              <ModalFooter>
                <button
                  className="btn btn-primary mt-3 w-100 h_50"
                  type="submit"
                  disabled={isLoading}
                >
                  <h5>{isLoading ? "Creating..." : "Create Sponsor"}</h5>
                </button>
              </ModalFooter>
            </Form>
          </ModalBody>
        </Modal>

        <Modal isOpen={editModal} toggle={() => setEditModal(false)}>
          <ModalHeader toggle={() => setEditModal(false)}>Edit Sponsor</ModalHeader>
          <ModalBody className="p-3">
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                editValidation.handleSubmit();
              }}
            >
              <FormGroup>
                <Label for="editSponsorName">Name</Label>
                <Input
                  id="editSponsorName"
                  name="sponsorName"
                  type="text"
                  onChange={editValidation.handleChange}
                  onBlur={editValidation.handleBlur}
                  value={editValidation.values.sponsorName}
                  invalid={editValidation.touched.sponsorName && !!editValidation.errors.sponsorName}
                />
                <FormFeedback>{getErrorMessage(editValidation.errors.sponsorName)}</FormFeedback>
              </FormGroup>

              <FormGroup>
                <Label for="editSponsorSelection">Sponsor Type</Label>
                <Input
                  id="editSponsorSelection"
                  name="sponsorSelection"
                  type="select"
                  value={editValidation.values.sponsorSelection}
                  onChange={editValidation.handleChange}
                  onBlur={editValidation.handleBlur}
                  invalid={editValidation.touched.sponsorSelection && !!editValidation.errors.sponsorSelection}
                >
                  <option value="">Select Sponsor Type</option>
                  {allSponsorTypes.map((type: any) => (
                    <option key={type.sponsorTypeId} value={type.sponsorTypeId}>
                      {type.title}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{getErrorMessage(editValidation.errors.sponsorSelection)}</FormFeedback>
              </FormGroup>

              <FormGroup>
                <Label for="editSponsorWebsiteUrl">Website URL</Label>
                <Input
                  id="editSponsorWebsiteUrl"
                  name="sponsorWebsiteUrl"
                  type="text"
                  onChange={editValidation.handleChange}
                  onBlur={editValidation.handleBlur}
                  value={editValidation.values.sponsorWebsiteUrl}
                  invalid={editValidation.touched.sponsorWebsiteUrl && !!editValidation.errors.sponsorWebsiteUrl}
                />
                <FormFeedback>{getErrorMessage(editValidation.errors.sponsorWebsiteUrl)}</FormFeedback>
              </FormGroup>

              <FormGroup>
                <Label for="editSponsorImage">Sponsor Image</Label>
                {editPreviewImage && (
                  <img
                    src={editPreviewImage}
                    alt="Current Sponsor Image"
                    style={{ width: "120px", height: "120px", objectFit: "cover", marginBottom: "10px", display: "block" }} 
                  />
                )}
                <Input
                  id="editSponsorImage"
                  name="thumb"
                  type="file"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) {
                      editValidation.setFieldValue("thumb", file);
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setEditPreviewImage(reader.result as string);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
              </FormGroup>

              <ModalFooter>
                <Button
                  className="btn btn-primary mt-3 w-100 h_50"
                  color="primary"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? "Saving..." : "Save Changes"}
                </Button>
              </ModalFooter>
            </Form>
          </ModalBody>
        </Modal>
      </div>

      <div className="mt-4">
        {sponsors && sponsors.length > 0 ? (
          sponsors.map((sponsor: any) => (
            <ListCard
              key={sponsor.sponsorId}
              cardData={{
                iconImage: sponsor.thumb,
                name: sponsor.name,
                ticket: sponsor.title,
                link: sponsor.link,
                startDate: sponsor.createdAt,
              }}
              onEdit={() => handleEditClick(sponsor)}
              onDelete={() => handleDelete(sponsor)}
            />
          ))
        ) : (
          <p className="text-muted">No sponsors available</p>
        )}
      </div>

      <ConfirmationDialog
        isOpen={deleteConfirm.isOpen}
        toggle={() => setDeleteConfirm({ isOpen: false, row: null })}
        onConfirm={confirmDelete}
        title="Delete Sponsor"
        message={`Are you sure you want to permanently delete <strong>${deleteConfirm.row?.name || 'this sponsor'}</strong>? This action cannot be undone.`}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        type="danger"
        icon="fa-solid fa-trash"
      />
    </div>
  );
};

export default UserSponsor;